/**
 * Utility functions for comparing and managing note arrays
 * 
 * These utilities help determine if there are changes between original saved notes
 * and currently selected notes, enabling smart UI behavior like conditionally
 * enabling/disabling the Apply button.
 */

/**
 * Compares two arrays of note IDs to determine if they contain the same elements,
 * regardless of order.
 * 
 * This function is order-independent, meaning [1, 2, 3] is considered equal to [3, 1, 2].
 * It handles edge cases like empty arrays, null, and undefined values.
 * 
 * @param {number[]} arr1 - First array of note IDs
 * @param {number[]} arr2 - Second array of note IDs
 * @returns {boolean} true if both arrays contain the same elements (regardless of order), false otherwise
 * 
 * @example
 * areNotesEqual([1, 2, 3], [3, 2, 1]) // returns true
 * areNotesEqual([1, 2], [1, 2, 3]) // returns false
 * areNotesEqual([], []) // returns true
 */
export const areNotesEqual = (arr1: number[], arr2: number[]): boolean => {
  // Sort and convert to string for order-independent comparison
  const sorted1 = [...arr1].sort().toString();
  const sorted2 = [...arr2].sort().toString();
  return sorted1 === sorted2;
};

/**
 * Determines if there have been changes in note selection by comparing
 * the original array with the currently selected array.
 * 
 * This is a convenience wrapper around areNotesEqual that inverts the logic,
 * making it easier to use in UI contexts where you want to know if changes exist.
 * 
 * @param {number[]} original - Array of original note IDs (from backend/saved state)
 * @param {number[]} selected - Array of currently selected note IDs (from UI state)
 * @returns {boolean} true if there are changes (arrays differ), false if no changes (arrays match)
 * 
 * @example
 * hasNotesChanged([1, 2], [1, 2, 3]) // returns true (note added)
 * hasNotesChanged([1, 2, 3], [1, 2]) // returns true (note removed)
 * hasNotesChanged([1, 2], [2, 1]) // returns false (same notes, different order)
 */
export const hasNotesChanged = (original: number[], selected: number[]): boolean => {
  return !areNotesEqual(original, selected);
};
